import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, MessageSquare } from 'lucide-react';
import { showError } from '@/utils/toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface ForumCategory {
  id: string;
  title: string;
  slug: string;
  description: string;
  iconName: string;
  color: string;
  parent_id: string | null;
  isHidden: boolean;
  isHighlighted: boolean;
}

interface ExistingCategory {
  id: string;
  title: string;
}

interface EditCategoryModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  category: ForumCategory | null;
  onSave: (data: Omit<ForumCategory, 'icon' | 'slug' | 'topicsCount' | 'commentsCount' | 'lastActivity' | 'latestTopics' | 'id'> & { id?: string; }) => void;
  availableIcons: string[];
  getIconComponent: (iconName: string) => JSX.Element;
  allCategories: ExistingCategory[];
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ isOpen, onOpenChange, category, onSave, availableIcons, getIconComponent, allCategories }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [iconName, setIconName] = useState('MessageSquare');
  const [color, setColor] = useState('gray');
  const [isSubcategory, setIsSubcategory] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [isHidden, setIsHidden] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [titleValidation, setTitleValidation] = useState<{ message: string; isError: boolean } | null>(null);

  useEffect(() => {
    if (category) {
      setTitle(category.title);
      setDescription(category.description);
      setIconName(category.iconName);
      setColor(category.color);
      setIsSubcategory(!!category.parent_id);
      setParentId(category.parent_id);
      setIsHidden(category.isHidden);
      setIsHighlighted(category.isHighlighted);
      setTitleValidation(null);
    } else {
      // Reset for new category
      setTitle('');
      setDescription('');
      setIconName('MessageSquare');
      setColor('gray');
      setIsSubcategory(false);
      setParentId(null);
      setIsHidden(false);
      setIsHighlighted(false);
      setTitleValidation(null);
    }
  }, [category, isOpen]);

  useEffect(() => {
    if (!title.trim()) {
      setTitleValidation(null);
      return;
    }

    const trimmedTitle = title.trim().toLowerCase();

    const isTitleTaken = allCategories.some(cat => {
      // When editing, ignore the current category's original title for the check
      if (category && cat.id === category.id) {
        return false;
      }
      return cat.title.trim().toLowerCase() === trimmedTitle;
    });

    if (isTitleTaken) {
      setTitleValidation({ message: 'Ez a név már foglalt.', isError: true });
    } else {
      setTitleValidation({ message: 'A név szabad.', isError: false });
    }
  }, [title, allCategories, category]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      showError('A cím és a leírás nem lehet üres!');
      return;
    }

    if (titleValidation?.isError) {
      showError('A kategória neve már foglalt. Válassz másikat!');
      return;
    }

    onSave({
      id: category?.id,
      title: title.trim(),
      description: description.trim(),
      iconName,
      color,
      parent_id: isSubcategory ? parentId : null,
      isHidden,
      isHighlighted,
    });
    onOpenChange(false);
  };

  const isSaveDisabled = !title.trim() || !description.trim() || !!titleValidation?.isError;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-card text-card-foreground border-border shadow-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary flex items-center gap-2">
            <MessageSquare size={24} /> {category ? 'Kategória szerkesztése' : 'Új kategória'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div>
            <Label htmlFor="edit-cat-title" className="text-muted-foreground">Cím</Label>
            <Input id="edit-cat-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-secondary" required />
            {titleValidation && (
              <p className={`mt-1 text-sm ${titleValidation.isError ? 'text-destructive' : 'text-green-500'}`}>
                {titleValidation.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="edit-cat-desc" className="text-muted-foreground">Leírás</Label>
            <Textarea id="edit-cat-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 bg-secondary min-h-[100px]" required />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <Label htmlFor="is-subcategory" className="text-muted-foreground">Alkategória?</Label>
            <Switch id="is-subcategory" checked={isSubcategory} onCheckedChange={setIsSubcategory} />
          </div>
          {isSubcategory && (
            <div>
              <Label htmlFor="parent-category" className="text-muted-foreground">Szülő kategória</Label>
              <Select value={parentId || ''} onValueChange={setParentId}>
                <SelectTrigger id="parent-category" className="w-full bg-secondary">
                  <SelectValue placeholder="Válassz szülő kategóriát..." />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {/* Csak a főkategóriákat (parent_id nélkülieket) listázzuk, és a szerkesztett kategóriát kizárjuk */}
                  {allCategories.filter(cat => !cat.parent_id && cat.id !== category?.id).map(parentCat => (
                    <SelectItem key={parentCat.id} value={parentCat.id}>{parentCat.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-muted-foreground">Ikon</Label>
            <div className="mt-2 grid grid-cols-6 gap-2 rounded-md border border-input bg-secondary p-2">
              {availableIcons.map((name) => (
                <button
                  type="button"
                  key={name}
                  onClick={() => setIconName(name)}
                  className={`flex h-12 w-12 items-center justify-center rounded-md transition-colors hover:bg-primary/20 ${iconName === name ? 'bg-primary text-primary-foreground ring-2 ring-ring ring-offset-2 ring-offset-background' : ''}`}
                  title={name}
                >
                  {React.cloneElement(getIconComponent(name), { size: 24 })}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="cat-color" className="text-muted-foreground">Szín</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger id="cat-color" className="w-full bg-secondary"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="blue">Kék</SelectItem>
                <SelectItem value="green">Zöld</SelectItem>
                <SelectItem value="purple">Lila</SelectItem>
                <SelectItem value="red">Piros</SelectItem>
                <SelectItem value="yellow">Sárga</SelectItem>
                <SelectItem value="gray">Szürke</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="cat-hidden" className="text-muted-foreground">Elrejtés</Label>
            <Switch id="cat-hidden" checked={isHidden} onCheckedChange={setIsHidden} />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="cat-highlighted" className="text-muted-foreground">Kiemelés</Label>
            <Switch id="cat-highlighted" checked={isHighlighted} onCheckedChange={setIsHighlighted} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégsem</Button>
            <Button type="submit" disabled={isSaveDisabled}><Send size={16} className="mr-2" /> Mentés</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCategoryModal;